import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useParams, useSearchParams } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Mosaic, MosaicWindow } from 'react-mosaic-component';
import { motion, AnimatePresence } from 'framer-motion';
import { useHotkeys } from 'react-hotkeys-hook';
import 'react-mosaic-component/react-mosaic-component.css';
import './styles/unity-theme.css';
import './styles/global.css';

// Components
import { MenuBar, LoadingScreen, AuthGuard } from './components';
import { HierarchyPanel } from './components/panels/HierarchyPanel';
import { AssetsPanel } from './components/panels/AssetsPanel';
import { ScenePanel, CodeEditorPanel, InspectorPanel, ConsolePanel, BuildPanel } from './components/panels';
import { AIChatPanel } from './components/panels/AIChatPanel';

// Stores
import { useIDEStore } from './stores/ideStore';
import { useAuthStore } from './stores/authStore';
import { useProjectStore } from './stores/projectStore';
import { WebSocketService } from './services/WebSocketService';

const queryClient = new QueryClient();

export type PanelType = 
  | 'hierarchy' 
  | 'scene' 
  | 'ai-chat' 
  | 'code-editor' 
  | 'inspector' 
  | 'assets' 
  | 'console' 
  | 'build';

const DEFAULT_LAYOUT = {
  direction: 'row' as const,
  first: {
    direction: 'column' as const,
    first: 'hierarchy',
    second: 'assets',
    splitPercentage: 60,
  },
  second: {
    direction: 'column' as const,
    first: {
      direction: 'row' as const,
      first: 'scene',
      second: 'ai-chat',
      splitPercentage: 65,
    },
    second: {
      direction: 'row' as const,
      first: 'code-editor',
      second: 'console',
      splitPercentage: 70,
    },
    splitPercentage: 60,
  },
  third: {
    direction: 'column' as const,
    first: 'inspector',
    second: 'build',
    splitPercentage: 70,
  },
  splitPercentage: 20,
};

function IDEWorkspace() {
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const userId = searchParams.get('userId');
  const goto = searchParams.get('goto'); // 'startphase2' or 'connect'
  const engine = searchParams.get('engine') as 'unity' | 'unreal' | 'godot' || 'unity';
  
  const { 
    layout, 
    setLayout, 
    isLoading, 
    setLoading,
    theme,
    toggleTheme 
  } = useIDEStore();
  
  const { 
    setToken, 
    isAuthenticated 
  } = useAuthStore();
  
  const { 
    loadProject, 
    currentProject 
  } = useProjectStore();

  // Coding phase state
  const [codingInProgress, setCodingInProgress] = useState(false);
  const [codingProgress, setCodingProgress] = useState(0);
  const [codingMessage, setCodingMessage] = useState('');
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    const initializeIDE = async () => {
      setLoading(true);
      
      try {
        if (token) {
          setToken(token);
          localStorage.setItem('stan_token', token);
        } else {
          const storedToken = localStorage.getItem('stan_token');
          if (storedToken) {
            setToken(storedToken);
          }
        }

        if (projectId && isAuthenticated) {
          await loadProject(projectId, engine);
          
          // Connect WebSocket
          if (userId) {
            WebSocketService.connect(projectId, userId, token || undefined);
          }
          
          // Handle goto parameter
          if (goto === 'startphase2' && currentProject?.status === 'phase1_complete') {
            WebSocketService.send({
              type: 'start_godot_phase2',
              projectId,
              userId
            });
          } else if (goto === 'fix_build') {
            // Trigger AI fix for build errors
            WebSocketService.requestFix(
              projectId,
              userId,
              'build_logs', // Virtual path for build logs
              'Build failed in GitHub Actions. Please analyze logs and fix project files.'
            );
          }
        }

      } catch (error) {
        console.error('Failed to initialize IDE:', error);
      } finally {
        setLoading(false);
      }
    };

    initializeIDE();
  }, [projectId, token, isAuthenticated, userId, engine]);

  // Listen for connection status
  useEffect(() => {
    const unsubscribe = WebSocketService.onConnectionChange((connected) => {
      setIsConnected(connected);
    });
    return unsubscribe;
  }, []);

  // Listen for coding phase updates
  useEffect(() => {
    const unsubscribe = WebSocketService.onMessage((message) => {
      switch (message.type) {
        case 'coding_phase_started':
        case 'coding_started':
        case 'phase2_started':
          setCodingInProgress(true);
          setCodingMessage('Stan is coding your game...');
          setCodingProgress(0);
          break;
          
        case 'task_started':
          setCodingInProgress(true);
          setCodingMessage(`Working on: ${message.taskTitle || 'Task'}`);
          if (message.progress) setCodingProgress(message.progress);
          break;
          
        case 'task_completed':
          // Optional: Add a small toast or log
          if (message.progress) setCodingProgress(message.progress);
          break;
          
        case 'phase2_complete':
        case 'coding_phase_complete':
          setCodingInProgress(false);
          setCodingProgress(100);
          break;
          
        case 'build_started':
          setCodingInProgress(true);
          setCodingMessage('Building Game...');
          setCodingProgress(100); // Pulse effect in UI maybe?
          break;
          
        case 'build_complete':
          setCodingInProgress(false);
          // Trigger build panel refresh or show success
          break;
      }
    });

    return unsubscribe;
  }, []);

  useHotkeys('ctrl+shift+p', () => {
    console.log('Command palette');
  });

  useHotkeys('f5', () => {
    console.log('Build and run');
  });

  const renderPanel = (id: PanelType) => {
    const panelProps = {
      key: id,
      className: 'stan-panel',
    };

    switch (id) {
      case 'hierarchy':
        return <HierarchyPanel {...panelProps} />;
      case 'scene':
        return <ScenePanel {...panelProps} />;
      case 'ai-chat':
        return <AIChatPanel {...panelProps} />;
      case 'code-editor':
        return <CodeEditorPanel {...panelProps} />;
      case 'inspector':
        return <InspectorPanel {...panelProps} />;
      case 'assets':
        return <AssetsPanel {...panelProps} />;
      case 'console':
        return <ConsolePanel {...panelProps} />;
      case 'build':
        return <BuildPanel {...panelProps} />;
      default:
        return <div className="stan-panel">Unknown panel: {id}</div>;
    }
  };

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <div className={`stan-ide ${theme}`}>
      {/* Coding in Progress Banner */}
      <AnimatePresence>
        {codingInProgress && (
          <motion.div 
            className="coding-banner"
            initial={{ y: -100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -100, opacity: 0 }}
          >
            <div className="banner-content">
              <span className="banner-icon">🤖</span>
              <span className="banner-text">
                {codingMessage} {Math.round(codingProgress * 100)}%
              </span>
              <div className="banner-progress">
                <div 
                  className="progress-bar" 
                  style={{ width: `${codingProgress * 100}%` }}
                />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <MenuBar />
      
      <div className="ide-workspace">
        <Mosaic<PanelType>
          renderTile={(id, path) => (
            <MosaicWindow<PanelType>
              path={path}
              title={id.charAt(0).toUpperCase() + id.slice(1).replace('-', ' ')}
              createNode={() => 'hierarchy'}
              toolbarControls={[]}
            >
              {renderPanel(id)}
            </MosaicWindow>
          )}
          value={layout || DEFAULT_LAYOUT}
          onChange={setLayout}
          className="mosaic-blueprint-theme"
        />
      </div>

      <div className="status-bar">
        <div className="status-left">
          <span className="status-item">
            {currentProject ? `Project: ${currentProject.name}` : 'No project loaded'}
          </span>
          <span className="status-item" style={{ color: isConnected ? '#4caf50' : '#f44336' }}>
            {isConnected ? '● Connected' : '○ Disconnected'}
          </span>
        </div>
        <div className="status-right">
          <span className="status-item">
            {currentProject ? `${currentProject.engine} ${currentProject.engineVersion}` : 'No engine loaded'}
          </span>
          <button onClick={toggleTheme}>
            {theme === 'dark' ? '🌙' : '☀️'}
          </button>
        </div>
      </div>
    </div>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <Routes>
          <Route 
            path="/project/:projectId" 
            element={
              <AuthGuard>
                <IDEWorkspace />
              </AuthGuard>
            } 
          />
          <Route 
            path="/" 
            element={
              <div className="welcome-screen">
                <h1>Stan IDE</h1>
                <p>AI-Powered Game Development Environment</p>
              </div>
            } 
          />
        </Routes>
      </Router>
    </QueryClientProvider>
  );
}

export default App;